<template>
  <crud-modal
    :show="show"
    title="Tokens de Acceso API"
    size="md"
    @close="$emit('close')"
  >
    <div class="tokens-modal-content">
      <p class="text-muted small mb-3">
        Al generar los códigos de acceso API Token y API Secret se le permitirá acceder a las APIs detalladas en los permisos del usuario. Para que el usuario pueda utilizar las claves debe tener al menos una API habilitada en los permisos.
      </p>

      <div
        v-if="user && (user.apitoken || user.api_token)"
        class="alert alert-warning d-flex align-items-start mb-3"
        role="alert"
      >
        <i class="bi bi-exclamation-triangle-fill text-warning mr-2 mt-1 font-size-18"></i>
        <div class="small">
          <strong>¡ATENCIÓN!</strong> Al regenerar los códigos de API Token y API Secret, el usuario
          <strong>no podrá acceder a las APIs</strong> que esté utilizando, hasta que actualice sus credenciales con los nuevos códigos generados.
        </div>
      </div>

      <div
        v-if="user && (user.apitoken || user.api_token)"
        class="card bg-light border-0 p-3 mb-3"
      >
        <!-- API Token -->
        <div class="mb-3">
          <label class="small font-weight-bold text-muted mb-1 d-flex justify-content-between">
            <span>API Token:</span>
            <button
              type="button"
              class="btn btn-link btn-xs p-0 text-primary"
              @click="copyToClipboard(tokenValue, 'token')"
              title="Copiar token"
            >
              <i :class="copiedToken ? 'bi-check-lg text-success' : 'bi-clipboard'"></i>
              <span class="ml-1">{{ copiedToken ? 'Copiado' : 'Copiar' }}</span>
            </button>
          </label>
          <div class="input-group input-group-sm">
            <input
              type="text"
              class="form-control font-mono bg-white"
              readonly
              :value="tokenValue"
            />
          </div>
        </div>

        <!-- API Secret -->
        <div>
          <label class="small font-weight-bold text-muted mb-1 d-flex justify-content-between">
            <span>API Secret:</span>
            <button
              type="button"
              class="btn btn-link btn-xs p-0 text-primary"
              @click="copyToClipboard(secretValue, 'secret')"
              title="Copiar secret"
            >
              <i :class="copiedSecret ? 'bi-check-lg text-success' : 'bi-clipboard'"></i>
              <span class="ml-1">{{ copiedSecret ? 'Copiado' : 'Copiar' }}</span>
            </button>
          </label>
          <div class="input-group input-group-sm">
            <input
              :type="showSecret ? 'text' : 'password'"
              class="form-control font-mono bg-white"
              readonly
              :value="secretValue"
            />
            <div class="input-group-append">
              <button
                class="btn btn-outline-secondary"
                type="button"
                @click="showSecret = !showSecret"
                :title="showSecret ? 'Ocultar secret' : 'Mostrar secret'"
              >
                <i :class="showSecret ? 'bi-eye-slash-fill' : 'bi-eye-fill'"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="alert alert-info small mb-0">
        <i class="bi bi-info-circle mr-1"></i> Este usuario aún no tiene tokens de API generados. Haga clic en <strong>Generar Tokens</strong> para crearlos.
      </div>
    </div>

    <template #footer>
      <button type="button" class="btn btn-secondary mr-2" @click="$emit('close')">
        Cerrar
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="loading"
        @click="generarTokens"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm mr-1"></span>
        <i v-else class="bi-recycle mr-1"></i>
        <span>{{ hasTokens ? 'Regenerar Tokens' : 'Generar Tokens' }}</span>
      </button>
    </template>
  </crud-modal>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import CrudModal from "../utiles/CrudModal.vue";

export default {
  name: "UsuarioTokensModal",
  components: {
    CrudModal,
  },
  props: {
    show: { type: Boolean, default: false },
    user: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      showSecret: false,
      copiedToken: false,
      copiedSecret: false,
      loading: false,
    };
  },
  computed: {
    tokenValue() {
      return (this.user && (this.user.apitoken || this.user.api_token)) || "";
    },
    secretValue() {
      return (this.user && (this.user.apisecret || this.user.api_secret)) || "";
    },
    hasTokens() {
      return Boolean(this.tokenValue);
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.showSecret = false;
        this.copiedToken = false;
        this.copiedSecret = false;
      }
    },
  },
  methods: {
    copyToClipboard(text, type) {
      if (!text) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement("textarea");
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      if (type === "token") {
        this.copiedToken = true;
        setTimeout(() => { this.copiedToken = false; }, 2000);
      } else {
        this.copiedSecret = true;
        setTimeout(() => { this.copiedSecret = false; }, 2000);
      }
    },

    generarTokens() {
      if (!this.user || !this.user.id_usuario) return;
      this.loading = true;
      axios
        .post(process.env.VUE_APP_API_URL + "usuario/gentokens", {
          id_usuario: this.user.id_usuario,
        })
        .then((response) => {
          const data = response.data;
          const newToken = data.apitoken || (data.data && data.data.apitoken);
          const newSecret = data.apisecret || (data.data && data.data.apisecret);

          Swal.fire({
            title: "¡Tokens generados!",
            text: "Los códigos de acceso API Token y API Secret se han generado con éxito.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });

          this.$emit("tokens-generated", {
            userId: this.user.id_usuario,
            apitoken: newToken,
            apisecret: newSecret,
          });
        })
        .catch((e) => {
          Swal.fire({
            title: "Error!",
            text: (e.response && e.response.data && e.response.data.error) || "Ha ocurrido un error al intentar generar tokens",
            icon: "error",
            confirmButtonText: "Aceptar",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.font-mono {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.85rem;
}
.btn-xs {
  font-size: 0.75rem;
}
</style>
